import {
  createContext,
  default as React,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { getCookie, setCookie } from '../helpers/cookie';

export type Theme = 'light' | 'dark';

type ThemeContextValue = {
  theme: Theme;
  toggleTheme: () => void;
};

const THEME_COOKIE_NAME = 'themeType';
const DEFAULT_THEME: Theme = 'dark';

const isTheme = (theme?: string | null): theme is Theme =>
  theme === 'light' || theme === 'dark';

const getStoredTheme = (): Theme => {
  const storedTheme = getCookie(THEME_COOKIE_NAME);

  return isTheme(storedTheme) ? storedTheme : DEFAULT_THEME;
};

const applyTheme = (theme: Theme) => {
  if (typeof window === 'undefined') {
    return;
  }

  document.documentElement.dataset.theme = theme;
  setCookie(THEME_COOKIE_NAME, theme);
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }

  return context;
};

type Props = {
  children: ReactNode;
};

export const ThemeProvider = ({ children }: Props) => {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => {
      const nextTheme = current === 'light' ? 'dark' : 'light';
      applyTheme(nextTheme);
      return nextTheme;
    });
  }, []);

  const value = useMemo(
    () => ({
      theme,
      toggleTheme,
    }),
    [theme, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export default ThemeContext;
